import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { API } from '@unboared/react';

import { ClientDatabase, IClient, Mode } from '~/types/client';
import type { RegiePhoneState, RegieWatch, RosterPlayer } from '~/types/screen/regie';
import { effectiveRegieView, REGIE_PHONE_KEY, regieReaction, ROSTER_MAX, safeAvatar } from '~/types/screen/regie';
import { trackLivePlayers } from '~/utils/liveGamepads';
import { usePresentation } from '~/stores/usePresentation';
import type { ClientBranding } from '~/services/branding/clientBranding';

import { PublicLobbyView } from './PublicLobbyView';
import styles from './PublicStage.module.css';

const ROSTER_DEBOUNCE_MS = 1500;
const CURSOR_IDLE_MS = 2500;

function readPlayers(): RosterPlayer[] {
    return API.getGamepadIDs()
        .slice(0, ROSTER_MAX)
        .map((id: string) => {
            const p = API.getPlayer(id);
            const name = (p?.username || '?').slice(0, 40);
            return { id, name, avatar: safeAvatar(p?.avatar), color: p?.color || undefined };
        });
}

/**
 * Joueurs connectés à l'écran public, en direct, publiés aussi dans le doc client
 * (`screenInfos.anim.roster`) pour la régie — seulement quand la liste change.
 */
function useLivePlayers(clientID: string) {
    const [players, setPlayers] = useState<RosterPlayer[]>([]);
    const idRef = useRef(clientID);
    idRef.current = clientID;

    useEffect(() => {
        let last = '';
        let timer: ReturnType<typeof setTimeout> | undefined;
        const refresh = () => {
            let next: RosterPlayer[];
            try {
                next = readPlayers();
            } catch {
                return; // session pas encore prête
            }
            const key = JSON.stringify(next);
            if (key === last) return;
            last = key;
            setPlayers(next);
            if (timer) clearTimeout(timer);
            timer = setTimeout(() => {
                ClientDatabase.setScreenRoster(idRef.current, Mode.Anim, next).catch(() => {
                    /* la régie verra la liste au prochain changement */
                });
            }, ROSTER_DEBOUNCE_MS);
        };
        const teardown = trackLivePlayers({
            onSeed: refresh,
            onConnect: refresh,
            onDisconnect: refresh,
            onDeviceBack: refresh,
            onReconcile: refresh,
        });
        return () => {
            if (timer) clearTimeout(timer);
            teardown();
            // Écran public fermé : la liste ne traîne pas dans la fiche de l'établissement.
            ClientDatabase.setScreenRoster(idRef.current, Mode.Anim, []).catch(() => {});
        };
    }, []);

    return players;
}

/** Lien des joueurs : la session s'ouvre un peu après le montage de la page. */
function useSessionLink() {
    const [link, setLink] = useState<string | null>(null);
    useEffect(() => {
        const read = () => {
            try {
                const l = API.getSessionLink();
                if (l) setLink((prev) => (prev === l ? prev : l));
            } catch {
                /* pas encore de session */
            }
        };
        read();
        const t = window.setInterval(read, 2000);
        return () => window.clearInterval(t);
    }, []);
    return link;
}

/**
 * Dit aux téléphones ce que montre l'écran public : hors jeu, ils affichent un écran
 * d'attente (components/dualScreen/PhoneWaitingScreen) au lieu de la manette du jeu.
 * Réessaie tant que la session n'est pas prête (l'état partagé est ensuite renvoyé par
 * la session à chaque téléphone qui arrive).
 */
function usePhoneState(state: RegiePhoneState) {
    const key = JSON.stringify(state);
    useEffect(() => {
        let timer: ReturnType<typeof setTimeout> | undefined;
        const send = () => {
            try {
                API.loadSharedProperty(API.getHostID(), REGIE_PHONE_KEY, JSON.parse(key));
            } catch {
                timer = setTimeout(send, 1500); // hôte pas encore prêt
            }
        };
        send();
        return () => clearTimeout(timer);
    }, [key]);
    // Écran public fermé : les téléphones retrouvent la manette du jeu.
    useEffect(
        () => () => {
            try {
                API.loadSharedProperty(API.getHostID(), REGIE_PHONE_KEY, null);
            } catch {
                /* session déjà fermée */
            }
        },
        []
    );
}

/** Curseur caché après quelques secondes d'immobilité (il passerait sinon sur la vidéo-projection). */
function useIdleCursor() {
    const [idle, setIdle] = useState(false);
    useEffect(() => {
        let t = window.setTimeout(() => setIdle(true), CURSOR_IDLE_MS);
        const wake = () => {
            setIdle(false);
            window.clearTimeout(t);
            t = window.setTimeout(() => setIdle(true), CURSOR_IDLE_MS);
        };
        window.addEventListener('mousemove', wake);
        window.addEventListener('mousedown', wake);
        return () => {
            window.clearTimeout(t);
            window.removeEventListener('mousemove', wake);
            window.removeEventListener('mousedown', wake);
        };
    }, []);
    return idle;
}

function useIsFullscreen() {
    const [full, setFull] = useState(() => !!document.fullscreenElement);
    useEffect(() => {
        const on = () => setFull(!!document.fullscreenElement);
        document.addEventListener('fullscreenchange', on);
        return () => document.removeEventListener('fullscreenchange', on);
    }, []);
    return full;
}

export type PublicStageProps = {
    client: IClient;
    branding: ClientBranding | null;
};

/**
 * Écran public du double écran (page TV ouverte avec `display=public`) : par-dessus
 * le jeu, l'accueil de la soirée ou un message plein écran, selon la régie. Aucune
 * commande : la régie pilote tout. Un double-clic met la page en plein écran (et
 * débloque le son, qu'un navigateur refuse à une fenêtre jamais touchée).
 */
export function PublicStage({ client, branding }: PublicStageProps) {
    const { t } = useTranslation();
    const players = useLivePlayers(client.id);
    const link = useSessionLink();
    const cursorIdle = useIdleCursor();
    const isFullscreen = useIsFullscreen();
    const [touched, setTouched] = useState(() => !!navigator.userActivation?.hasBeenActive);

    useEffect(() => {
        usePresentation.getState().setPublic(true);
        return () => usePresentation.getState().setPublic(false);
    }, []);

    const navigate = useNavigate();
    const screen = client.getScreen(Mode.Anim);
    const regie = screen?.regie;
    const view = effectiveRegieView(regie);
    usePhoneState({
        view,
        title: (regie?.eventName ?? '').trim() || client.name,
        message: view === 'message' ? (regie?.message ?? '') : '',
        ticker: regie?.tickerOn ? (regie.ticker ?? '') : '',
    });

    // L'accueil reste affiché jusqu'au LANCEMENT d'une partie (régie, téléphone), pas au
    // simple choix d'un jeu (« Changer de jeu ») : l'animateur prépare le jeu suivant
    // pendant que la salle voit l'accueil, partie en pause dessous. Détail : regieReaction.
    const watch: RegieWatch = {
        game: screen?.gameID ?? '',
        launch: screen?.launchRequest ?? 0,
        paused: !!screen?.paused,
        changed: screen?.gameChangedAt ?? 0,
    };
    const seenRef = useRef<RegieWatch | null>(null);
    useEffect(() => {
        const prev = seenRef.current;
        seenRef.current = watch;
        const reaction = regieReaction(prev, watch, view);
        if (!reaction) return;
        // « pause » : même vue, pause remise (une seule écriture, cf setRegieView).
        ClientDatabase.setRegieView(client.id, Mode.Anim, reaction === 'show-game' ? 'game' : view).catch(() => {});
    }, [watch.game, watch.launch, watch.paused, watch.changed, view]);

    const toggleFullscreen = () => {
        setTouched(true);
        if (document.fullscreenElement) return;
        document.documentElement.requestFullscreen?.().catch(() => {
            /* refusé (réglage, F11 déjà actif) : la fenêtre reste utilisable telle quelle */
        });
    };
    // Sorti du plein écran (Échap) : un double-clic hors du jeu y revient (le jeu, une
    // autre page, garde ses clics), sinon le bouton du coin.
    const toggleRef = useRef(toggleFullscreen);
    toggleRef.current = toggleFullscreen;
    useEffect(() => {
        const onDbl = () => toggleRef.current();
        window.addEventListener('dblclick', onDbl);
        return () => window.removeEventListener('dblclick', onDbl);
    }, []);

    // Consigne de plein écran : au centre la 1re fois ; pendant un jeu (écran rechargé
    // en pleine partie), discrète dans un coin et seulement quelques secondes.
    const [hintGone, setHintGone] = useState(false);
    useEffect(() => {
        if (view !== 'game') return undefined;
        const id = window.setTimeout(() => setHintGone(true), 10_000);
        return () => window.clearTimeout(id);
    }, [view]);
    const showHint = !touched && !isFullscreen && !(view === 'game' && hintGone);

    // Sortie de secours : sans régie (téléphone pas connecté), l'écran public ne doit pas
    // être une impasse. Visible seulement quand la souris bouge, jamais pour la salle.
    const leave = () => navigate('/home');

    return (
        <>
            {view !== 'game' && (
                <div className={styles.layer}>
                    <PublicLobbyView
                        eventName={regie?.eventName ?? ''}
                        venueName={client.name}
                        brand={branding}
                        link={link}
                        players={players}
                        ticker={regie?.tickerOn ? regie.ticker : ''}
                        message={view === 'message' ? regie?.message || ' ' : null}
                    />
                </div>
            )}
            {/* Bouclier transparent qui cache le curseur, seulement sur l'accueil et le
                message : au-dessus du jeu il volerait des clics (la souris qui bouge dans
                l'iframe d'un jeu n'arrive pas jusqu'ici). Les jeux cachent eux-mêmes leur
                curseur sur l'écran public. */}
            {cursorIdle && view !== 'game' && <div className={styles.cursorShield} aria-hidden="true" />}
            {!cursorIdle && (
                <div className={styles.corner}>
                    {!isFullscreen && (
                        <button type="button" onClick={toggleFullscreen}>
                            {t('regie.public.fullscreen')}
                        </button>
                    )}
                    <button type="button" onClick={leave}>
                        {t('regie.public.exit')}
                    </button>
                </div>
            )}
            {showHint && (
                <button
                    type="button"
                    className={view === 'game' ? styles.fullscreenHintSmall : styles.fullscreenHint}
                    onClick={toggleFullscreen}
                >
                    <strong>{t('regie.public.fullscreenTitle')}</strong>
                    <span>{t('regie.public.fullscreenHint')}</span>
                </button>
            )}
        </>
    );
}
